import { useCallback, useState } from 'react';
import { AppModal, AppRemixIcon, AppNoInfo } from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import styled from 'styled-components';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import Translator from '../../../../../components/helpers/Translator';
import EditorTranslatable from '../../../../../components/form/Texts/EditorTranslatable';
import { axiosPath } from '../../../../../../routing/helpers/router';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import AuxSidebar from '../../../../../Sidebar/AuxSidebar';
import colors from '../../../../../../constants/colors';
import { reorder } from '../../../../../../minions/forms';
import _ from 'lodash';
import InputTranslatable from '@/layout/components/form/Inputs/InputTranslatable';

export default function InternalObligationAdd({ internalObligationTemplates }) {
  const { moduleBridgeHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const auxSidebar = useSelector(
    (state) => state.currentPage?.auxSidebar?.opened
  );
  const [showModal, setShowModal] = useState(false);
  const [
    addEvidenceAdvancedChecklist,
    setAddEvidenceAdvancedChecklist,
  ] = useState({});
  const [validationSchema] = useState(
    Yup.object().shape({
      obligation: Yup.mixed()
        .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    })
  );

  const handleAddInternalObligation = useCallback(
    (payload) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_settings_internalObligation_add"
          ),
          {
            moduleBridgeHex,
            payload,
            internalObligationTemplateId: showModal,
          }
        )
        .then(() => {
          if (isMounted.current) {
            window.location.reload();
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [showModal]
  );

  const handleAuxSidebar = () => {
    dispatch(
      currentPageAction.change('auxSidebar', {
        opened:
          auxSidebar !== 'internalObligationTemplates'
            ? 'internalObligationTemplates'
            : null,
      })
    );
  };

  const handleResortAdvanceEvidences = useCallback(
    (evidenceAdvancedChecklist, data, sfv) => {
      const newOrder = reorder(
        evidenceAdvancedChecklist,
        data.source.index,
        data.destination.index
      );

      sfv(
        'evidenceAdvancedChecklist',
        newOrder?.map((eac, i) => ({ ...eac, sort: i }))
      );
    },
    []
  );

  return (
    <div>
      <span
        className="btn btn-xs btn-outline-light"
        onClick={() =>
          internalObligationTemplates.length === 1
            ? setShowModal(internalObligationTemplates?.[0]?.id)
            : handleAuxSidebar()
        }
      >
        <Translator
          file="module-legalCompliance"
          name="new-internal-obligation"
        />
      </span>
      <AuxSidebar
        title={ReactDOMServer.renderToString(
          <Translator file="module-resourceManager" name="resource.templates" />
        )}
        isOpen={auxSidebar === 'internalObligationTemplates'}
        width="350px"
        position="left"
        onClose={handleAuxSidebar}
      >
        {internalObligationTemplates !== undefined ? (
          <>
            {internalObligationTemplates.length > 0 ? (
              <List>
                {internalObligationTemplates?.map((t) => (
                  <li
                    key={t.id}
                    onClick={() => {
                      dispatch(
                        currentPageAction.change('auxSidebar', {
                          opened: null,
                        })
                      );
                      setShowModal(t.id);
                    }}
                  >
                    <span className="sidebar-element">
                      <b>{t.name}</b>
                    </span>
                  </li>
                ))}
              </List>
            ) : (
              <AppNoInfo
                text={
                  <Translator
                    file="module-resourceManager"
                    name="resource.no.internal.obligations"
                  />
                }
                icon={
                  <AppRemixIcon
                    icon="file-unknow"
                    color={colors.grey500}
                    size={44}
                  />
                }
                minHeight={250}
              />
            )}
          </>
        ) : (
          <AppNoInfo
            text={
              <Translator
                file="module-resourceManager"
                name="resource.loading"
              />
            }
            minHeight={250}
          />
        )}
      </AuxSidebar>
      {showModal && (
        <AppModal
          show={showModal}
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-legalCompliance"
              name="new-internal-obligation"
            />
          )}
          onClose={() => setShowModal(false)}
          size="xl"
        >
          <Formik
            initialValues={{
              obligation: '',
              evidenceAdvancedChecklist: [],
              nextDate: '',
            }}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleAddInternalObligation}
          >
            {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
              <>
                <div className="row mb-2">
                  <div className="col-lg-12">
                    <EditorTranslatable
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="obligation-text"
                        />
                      )}
                      icon={<AppRemixIcon icon="file-text" />}
                      onChange={(opt) => setFieldValue('obligation', opt)}
                      value={values.obligation}
                      error={errors.obligation}
                      className=""
                      toolbar={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ color: [] }, { background: [] }],
                          ['clean'],
                          [{ direction: 'rtl' }],
                        ],
                      }}
                      height={200}
                    />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-lg-6">
                    <h5>
                      <Translator
                        file="module-resourceManager"
                        name="resource.checklist"
                      />
                    </h5>
                    <AdvancedEvidenceList>
                      <DragDropContext
                        onDragEnd={(r) =>
                          handleResortAdvanceEvidences(
                            values?.evidenceAdvancedChecklist,
                            r,
                            setFieldValue
                          )
                        }
                      >
                        <Droppable
                          droppableId="droppable"
                          isDropDisabled={false}
                        >
                          {(providedDroppable) => (
                            <div
                              {...providedDroppable.droppableProps}
                              ref={providedDroppable.innerRef}
                            >
                              <FieldArray
                                name="evidenceAdvancedChecklist"
                                render={(evidenceAdvancedChecklist) => (
                                  <>
                                    {values?.evidenceAdvancedChecklist?.map(
                                      (eac, i) => (
                                        <Draggable
                                          key={i}
                                          draggableId={i.toString()}
                                          index={i}
                                          isDragDisabled={false}
                                        >
                                          {(providedDraggable) => (
                                            <li
                                              key={i}
                                              ref={providedDraggable.innerRef}
                                              {...providedDraggable.draggableProps}
                                              {...providedDraggable.dragHandleProps}
                                            >
                                              <AppRemixIcon
                                                icon="arrow-up-down"
                                                className="mb-3"
                                                title={ReactDOMServer.renderToString(
                                                  <Translator
                                                    file="module-resourceManager"
                                                    name="resource.reorder"
                                                  />
                                                )}
                                              />
                                              <InputTranslatable
                                                className="name mb-3"
                                                onChange={(v) =>
                                                  setFieldValue(
                                                    `evidenceAdvancedChecklist[${i}].name`,
                                                    v
                                                  )
                                                }
                                                values={
                                                  values
                                                    ?.evidenceAdvancedChecklist?.[
                                                    i
                                                  ]?.name
                                                }
                                                error={
                                                  errors
                                                    ?.evidenceAdvancedChecklist?.[
                                                    i
                                                  ]?.name
                                                }
                                              />
                                              <button
                                                type="button"
                                                title={ReactDOMServer.renderToString(
                                                  <Translator
                                                    file="module-resourceManager"
                                                    name="resource.delete"
                                                  />
                                                )}
                                                className="btn btn-sm btn-danger mb-3"
                                                onClick={() => {
                                                  evidenceAdvancedChecklist.remove(
                                                    i
                                                  );
                                                }}
                                              >
                                                <AppRemixIcon icon="delete-bin" />
                                              </button>
                                            </li>
                                          )}
                                        </Draggable>
                                      )
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                      <li className="mt-2">
                        <InputTranslatable
                          onChange={(v) => setAddEvidenceAdvancedChecklist(v)}
                          values={addEvidenceAdvancedChecklist}
                        />
                        <button
                          type="button"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.add"
                            />
                          )}
                          className="btn btn-sm btn-success mb-3"
                          disabled={_.isEmpty(addEvidenceAdvancedChecklist)}
                          onClick={() => {
                            values?.evidenceAdvancedChecklist.push({
                              name: addEvidenceAdvancedChecklist,
                              sort:
                                values?.evidenceAdvancedChecklist?.length || 0,
                            });
                            setAddEvidenceAdvancedChecklist({});
                          }}
                        >
                          <AppRemixIcon icon="add" />
                        </button>
                      </li>
                    </AdvancedEvidenceList>
                  </div>
                  <div className="col-lg-6">
                    <EditorTranslatable
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="periodicity-text"
                        />
                      )}
                      icon={<AppRemixIcon icon="file-text" />}
                      onChange={(opt) => setFieldValue('nextDate', opt)}
                      value={values.nextDate}
                      error={errors.nextDate}
                      className=""
                      toolbar={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ color: [] }, { background: [] }],
                          ['clean'],
                          [{ direction: 'rtl' }],
                        ],
                      }}
                      height={200}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12">
                    <button
                      className="btn btn-success btn-block"
                      onClick={handleSubmit}
                      type="submit"
                      disabled={isSubmitting}
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="create-obligation"
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
      )}
    </div>
  );
}

const List = styled.div`
  list-style: none;

  li {
    cursor: pointer;
    padding: 7px 10px;
  }
`;

const AdvancedEvidenceList = styled.ul`
  list-style: none;
  padding: 0;
  li {
    display: flex;
    align-items: center;
    gap: 5px;
    & > div {
      flex-grow: 1;
    }
  }
`;
